// =============================================================================
// FORM SETTINGS PANEL - Professional form configuration like SurveyJS
// =============================================================================

import { X, Palette, Settings2, CheckCircle, Layout } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Separator } from '../../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { cn } from '../../../lib/utils';
import { useFormBuilderStore } from '../store';
import type { FormSettings, FormTheme } from '../types';

interface FormSettingsPanelProps {
  onClose: () => void;
}

export function FormSettingsPanel({ onClose }: FormSettingsPanelProps) {
  const { settings, theme, updateSettings, updateTheme, form, updateFormName, updateFormDescription } =
    useFormBuilderStore();

  const handleSettingsChange = (updates: Partial<FormSettings>) => {
    updateSettings(updates);
  };

  const handleThemeChange = (updates: Partial<FormTheme>) => {
    updateTheme(updates);
  };

  // Color presets
  const colorPresets = [
    { name: 'Bleu', primary: '#3B82F6' },
    { name: 'Violet', primary: '#8B5CF6' },
    { name: 'Vert', primary: '#10B981' },
    { name: 'Rouge', primary: '#EF4444' },
    { name: 'Orange', primary: '#F97316' },
    { name: 'Rose', primary: '#EC4899' },
    { name: 'Cyan', primary: '#06B6D4' },
    { name: 'Indigo', primary: '#6366F1' },
  ];

  return (
    <div className="flex flex-col h-full bg-white dark:bg-secondary-800">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
        <h3 className="font-semibold text-secondary-900 dark:text-white">
          Paramètres du formulaire
        </h3>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="general" className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="px-4 pt-2 justify-start bg-transparent border-b border-secondary-200 dark:border-secondary-700 rounded-none">
          <TabsTrigger value="general" className="gap-1.5">
            <Settings2 className="h-3.5 w-3.5" />
            Général
          </TabsTrigger>
          <TabsTrigger value="theme" className="gap-1.5">
            <Palette className="h-3.5 w-3.5" />
            Thème
          </TabsTrigger>
          <TabsTrigger value="submit" className="gap-1.5">
            <CheckCircle className="h-3.5 w-3.5" />
            Soumission
          </TabsTrigger>
          <TabsTrigger value="layout" className="gap-1.5">
            <Layout className="h-3.5 w-3.5" />
            Mise en page
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="flex-1 overflow-y-auto p-4 space-y-6 mt-0">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom du formulaire</Label>
              <Input
                value={form?.name || ''}
                onChange={(e) => updateFormName(e.target.value)}
                placeholder="Mon formulaire"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form?.description || ''}
                onChange={(e) => updateFormDescription(e.target.value)}
                placeholder="Description du formulaire..."
                rows={3}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Barre de progression</Label>
                <p className="text-xs text-secondary-500">
                  Afficher une barre de progression pour les formulaires multi-pages
                </p>
              </div>
              <Switch
                checked={settings.showProgressBar}
                onCheckedChange={(checked) =>
                  handleSettingsChange({ showProgressBar: checked })
                }
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Numéros de pages</Label>
                <p className="text-xs text-secondary-500">
                  Afficher le numéro de page actuel
                </p>
              </div>
              <Switch
                checked={settings.showPageNumbers}
                onCheckedChange={(checked) =>
                  handleSettingsChange({ showPageNumbers: checked })
                }
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Indicateur obligatoire</Label>
                <p className="text-xs text-secondary-500">
                  Afficher un astérisque (*) pour les champs obligatoires
                </p>
              </div>
              <Switch
                checked={settings.showRequiredAsterisk}
                onCheckedChange={(checked) =>
                  handleSettingsChange({ showRequiredAsterisk: checked })
                }
              />
            </div>
          </div>
        </TabsContent>

        {/* Theme Tab */}
        <TabsContent value="theme" className="flex-1 overflow-y-auto p-4 space-y-6 mt-0">
          <div className="space-y-4">
            <div className="space-y-3">
              <Label>Couleur principale</Label>
              <div className="grid grid-cols-4 gap-2">
                {colorPresets.map((preset) => (
                  <button
                    key={preset.primary}
                    type="button"
                    onClick={() => handleThemeChange({ primaryColor: preset.primary })}
                    className={cn(
                      'flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all',
                      theme.primaryColor === preset.primary
                        ? 'border-primary-500'
                        : 'border-secondary-200 dark:border-secondary-700 hover:border-secondary-300'
                    )}
                  >
                    <div
                      className="w-8 h-8 rounded-full"
                      style={{ backgroundColor: preset.primary }}
                    />
                    <span className="text-xs text-secondary-600 dark:text-secondary-400">
                      {preset.name}
                    </span>
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={theme.primaryColor}
                  onChange={(e) => handleThemeChange({ primaryColor: e.target.value })}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={theme.primaryColor}
                  onChange={(e) => handleThemeChange({ primaryColor: e.target.value })}
                  placeholder="#3B82F6"
                  className="flex-1 font-mono"
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Couleur de fond</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={theme.backgroundColor}
                  onChange={(e) => handleThemeChange({ backgroundColor: e.target.value })}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={theme.backgroundColor}
                  onChange={(e) => handleThemeChange({ backgroundColor: e.target.value })}
                  placeholder="#FFFFFF"
                  className="flex-1 font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Couleur du texte</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={theme.textColor}
                  onChange={(e) => handleThemeChange({ textColor: e.target.value })}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={theme.textColor}
                  onChange={(e) => handleThemeChange({ textColor: e.target.value })}
                  placeholder="#1F2937"
                  className="flex-1 font-mono"
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Coins arrondis</Label>
              <Select
                value={theme.borderRadius}
                onValueChange={(value) =>
                  handleThemeChange({ borderRadius: value as FormTheme['borderRadius'] })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  <SelectItem value="sm">Petit</SelectItem>
                  <SelectItem value="md">Moyen</SelectItem>
                  <SelectItem value="lg">Grand</SelectItem>
                  <SelectItem value="xl">Très grand</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Taille du texte</Label>
              <Select
                value={theme.fontSize}
                onValueChange={(value) =>
                  handleThemeChange({ fontSize: value as FormTheme['fontSize'] })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sm">Petit</SelectItem>
                  <SelectItem value="md">Moyen</SelectItem>
                  <SelectItem value="lg">Grand</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Espacement</Label>
              <Select
                value={theme.spacing}
                onValueChange={(value) =>
                  handleThemeChange({ spacing: value as FormTheme['spacing'] })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="compact">Compact</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="relaxed">Aéré</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>

        {/* Submit Tab */}
        <TabsContent value="submit" className="flex-1 overflow-y-auto p-4 space-y-6 mt-0">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Texte du bouton Envoyer</Label>
              <Input
                value={settings.submitLabel}
                onChange={(e) => handleSettingsChange({ submitLabel: e.target.value })}
                placeholder="Envoyer"
              />
            </div>

            <div className="space-y-2">
              <Label>Texte du bouton Réinitialiser</Label>
              <Input
                value={settings.resetLabel || ''}
                onChange={(e) => handleSettingsChange({ resetLabel: e.target.value })}
                placeholder="Réinitialiser"
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Message de succès</Label>
              <Textarea
                value={settings.successMessage}
                onChange={(e) => handleSettingsChange({ successMessage: e.target.value })}
                placeholder="Merci pour votre soumission !"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>URL de redirection (optionnel)</Label>
              <Input
                value={settings.redirectUrl || ''}
                onChange={(e) => handleSettingsChange({ redirectUrl: e.target.value })}
                placeholder="https://..."
              />
              <p className="text-xs text-secondary-500">
                Redirige l'utilisateur après soumission
              </p>
            </div>

            <Separator />

            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Soumissions multiples</Label>
                <p className="text-xs text-secondary-500">
                  Autoriser plusieurs soumissions par utilisateur
                </p>
              </div>
              <Switch
                checked={settings.allowMultipleSubmissions}
                onCheckedChange={(checked) =>
                  handleSettingsChange({ allowMultipleSubmissions: checked })
                }
              />
            </div>
          </div>
        </TabsContent>

        {/* Layout Tab */}
        <TabsContent value="layout" className="flex-1 overflow-y-auto p-4 space-y-6 mt-0">
          <div className="space-y-4">
            <div className="space-y-3">
              <Label>Style de mise en page</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'default', label: 'Par défaut', desc: 'Simple et propre' },
                  { value: 'card', label: 'Carte', desc: 'Avec ombre et bordure' },
                  { value: 'minimal', label: 'Minimal', desc: 'Sans bordures' },
                  { value: 'bordered', label: 'Bordures', desc: 'Bordures visibles' },
                ].map((layout) => (
                  <button
                    key={layout.value}
                    type="button"
                    onClick={() =>
                      handleSettingsChange({ layout: layout.value as FormSettings['layout'] })
                    }
                    className={cn(
                      'p-4 rounded-lg border-2 text-left transition-all',
                      settings.layout === layout.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-secondary-200 dark:border-secondary-700 hover:border-secondary-300'
                    )}
                  >
                    <p className="font-medium text-sm text-secondary-900 dark:text-white">
                      {layout.label}
                    </p>
                    <p className="text-xs text-secondary-500">{layout.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <Label>Position des labels</Label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'top', label: 'Au-dessus', icon: '⬆️' },
                  { value: 'left', label: 'À gauche', icon: '⬅️' },
                  { value: 'floating', label: 'Flottant', icon: '✨' },
                ].map((position) => (
                  <button
                    key={position.value}
                    type="button"
                    onClick={() =>
                      handleSettingsChange({
                        labelPosition: position.value as FormSettings['labelPosition'],
                      })
                    }
                    className={cn(
                      'p-3 rounded-lg border-2 text-center transition-all',
                      settings.labelPosition === position.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-secondary-200 dark:border-secondary-700 hover:border-secondary-300'
                    )}
                  >
                    <span className="text-lg mb-1 block">{position.icon}</span>
                    <p className="text-xs font-medium text-secondary-700 dark:text-secondary-300">
                      {position.label}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
